import "../../../assets/scss/installation-style.scss";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "../../store/hooks";

export const Installation = () => {
  const { t } = useTranslation();
  const userState = useAppSelector((state: any) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    if (userState.is_provider && userState.is_initialized === false) {
      navigate("/InitializeProvider");
    }
  }, [userState]);

  return (
    <div className="wrapper installation-info">
      <h4>{t("Tutorial on using NeuroPlus Execution Agent")}</h4>
      <p>
        {t("Tutp1")}
        <a
          href="https://storage.googleapis.com/neurowatt-gplatform/install-agent/neurowatt"
          target="_blank"
          rel="noopener noreferrer"
        >
          &nbsp;{t("Link")}&nbsp;
        </a>
        {t("Tutp2")}
        <a
          href="https://storage.googleapis.com/neurowatt-gplatform/install-agent/default.yaml"
          target="_blank"
          rel="noopener noreferrer"
        >
          &nbsp;{t("Link")}&nbsp;
        </a>
        {t("Tutp3")}
      </p>

      <h4>{t("Server Specs")}</h4>
      <table className="spec-table">
        <thead>
          <tr>
            <th>{t("GPU Amount")}</th>
            <th>{t("CPU Cores")}</th>
            <th>RAM</th>
            <th>{t("Storage Space")}</th>
            <th>{t("Download Speed")}</th>
            <th>{t("Upload Speed")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>8</td>
            <td>32GB</td>
            <td>1TB</td>
            <td>100Mbps</td>
            <td>50Mbps</td>
          </tr>
          <tr>
            <td>2</td>
            <td>12</td>
            <td>48GB</td>
            <td>1.4TB</td>
            <td>100Mbps</td>
            <td>50Mbps</td>
          </tr>
          <tr>
            <td>4</td>
            <td>20</td>
            <td>80GB</td>
            <td>2.4TB</td>
            <td>200Mbps</td>
            <td>100Mbps</td>
          </tr>
          <tr>
            <td>8</td>
            <td>36</td>
            <td>144GB</td>
            <td>4.4TB</td>
            <td>400Mbps</td>
            <td>200Mbps</td>
          </tr>
        </tbody>
      </table>

      <h4>{t("Prerequisite")}</h4>
      <ol>
        <li>{t("install")} nvidia driver</li>
        <li>
          {t("install")}{" "}
          <a
            href="https://docs.nvidia.com/datacenter/cloud-native/container-toolkit/latest/install-guide.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            nvidia-container-toolkit
          </a>
        </li>
        <li>
          {t("pre3")}
          <div className="code">
            sudo apt install nvidia-fabricmanager-550=550.127.08-1
            <br />
            sudo systemctl start nvidia-fabricmanager
            <br />
            sudo systemctl enable nvidia-fabricmanager
            <br />
            # check
            <br />
            sudo systemctl status nvidia-fabricmanager
          </div>
          <ul>
            <li>{t("preversion1")}</li>
            <li>{t("preversion2")}</li>
          </ul>
        </li>
        <li>{t("pre4")}</li>
      </ol>

      <h5>1. {t("ExecutionParams")}</h5>
      <table>
        <thead>
          <tr>
            <th>{t("Params")}</th>
            <th>{t("Descriptions")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>-benchmark</td>
            <td>{t("RunBenchmarkTest")}</td>
          </tr>
          <tr>
            <td>-report</td>
            <td>{t("GenerateReport")}</td>
          </tr>
          <tr>
            <td>-add-user</td>
            <td>{t("CreateLocalUser")}</td>
          </tr>
          <tr>
            <td>-register</td>
            <td>{t("RegisterNeuroPlus")}</td>
          </tr>
          <tr>
            <td>-unregister</td>
            <td>{t("UnregisterNeuroPlus")}</td>
          </tr>
          <tr>
            <td>-install</td>
            <td>{t("InstallClusterAgent")}</td>
          </tr>
          <tr>
            <td>-uninstall</td>
            <td>{t("UninstallClusterAgent")}</td>
          </tr>
        </tbody>
      </table>
      <blockquote>{t("Blockquote")}</blockquote>

      <h5>2. Quick Start</h5>
      <pre>
        <code>
          sudo HOSTREPORTER_CONFIG=./default.yaml ./NeuroPlus -add-user
          -benchmark -install -register
        </code>
      </pre>
      <ol>
        <li>{t("RunPerformanceTest")}</li>
        <li>{t("CreateLocalUser")}</li>
        <li>{t("InstallClusterAgent")}</li>
        <li>{t("RegisterHost")}</li>
      </ol>

      <h5>3. {t("Common Exec Exp")}</h5>
      {[
        "benchmark",
        "report",
        "add-user",
        "install",
        "uninstall",
        "register",
        "unregister",
      ].map((cmd) => (
        <div key={cmd}>
          <h5>
            3.
            {[
              "benchmark",
              "report",
              "add-user",
              "install",
              "uninstall",
              "register",
              "unregister",
            ].indexOf(cmd) + 1}{" "}
            {t("Execute")} <i>{cmd}</i>
          </h5>
          <pre>
            <code>
              sudo HOSTREPORTER_CONFIG=./default.yaml ./NeuroPlus -{cmd}
            </code>
          </pre>
        </div>
      ))}

      <h5>4. {t("Precautions")}</h5>
      <ul>
        <li>{t("RequireAtLeastOneParameter")}</li>
        <li>{t("InstallUninstallMutualExclusive")}</li>
        <li>{t("RegisterUnregisterMutualExclusive")}</li>
        <li>{t("FollowErrorOrContactMaintainer")}</li>
      </ul>

      <h5>5. {t("AdvUsage")}</h5>
      <pre>
        <code>./neurowatt -benchmark -report</code>
      </pre>

      <h5>6. {t("ContactSupport")}</h5>
      <p>
        {t("CSNotes")} &nbsp;
        <a href="mailto:hanzel@neurowatt.ai">hanzel@neurowatt.ai</a>
      </p>
    </div>
  );
};
